import { useMemo, useState, type ReactNode } from 'react';
import ReactECharts from 'echarts-for-react';
import { Check } from 'lucide-react';
import type { EngagementFieldAvailability } from '@/api/generated/model';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CHART_INK, tone, useIsDarkMode } from '@/lib/chartTheme';
import { cn } from '@/lib/utils';
import { engagementColor, ENGAGEMENT_LOCATION_HELP } from '../lib/engagementColors';
import { EngagementChartHeader } from './EngagementChartFilters';
import { EngagementBusy, NotReadyBadge, NotReadyNote, engagementReadyState } from './EngagementNotReady';

export type TimeseriesView = {
  availability?: EngagementFieldAvailability;
  note?: string;
  periods: string[];
  series: { name: string; category?: string; total: number; values: number[] }[];
};

type ChartFilterProps = {
  range: string;
  onRangeChange: (range: string) => void;
  startDate?: string;
  endDate?: string;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  isLoading?: boolean;
};

const compact = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Figma legend pill: filled dot with a check, dimmed while the series is hidden. */
function LegendChip({
  name,
  color,
  hidden,
  onToggle,
}: {
  name: string;
  color: string;
  hidden: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={!hidden}
      className={cn(
        'flex items-center gap-2 text-sm transition-opacity',
        hidden ? 'opacity-40' : 'opacity-100',
      )}
    >
      <span
        className="flex size-4 items-center justify-center rounded-full"
        style={{ backgroundColor: color }}
      >
        <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
      </span>
      {name}
    </button>
  );
}

function useHiddenSeries() {
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const toggle = (name: string) => setHidden((current) => ({ ...current, [name]: !current[name] }));
  return { hidden, toggle };
}

/** Figma "Monetisation Engagement": stacked transaction counts per bucket. */
export function MonetisationEngagementCard({
  data,
  ...filters
}: { data: TimeseriesView } & ChartFilterProps) {
  const isDark = useIsDarkMode();
  const { hidden, toggle } = useHiddenSeries();
  const state = engagementReadyState(data.availability);

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      grid: tone(CHART_INK.grid, isDark),
      surface: tone(CHART_INK.surface, isDark),
    };
    // Stack bottom-up so the Figma order (Purchases, Subscriptions, Tips) reads top-down.
    const stackOrder = [...data.series].reverse().filter((series) => !hidden[series.name]);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
      },
      legend: { show: false },
      grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: data.periods,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { color: ink.muted, fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        splitLine: { show: false },
        axisLabel: { show: false },
      },
      series: stackOrder.map((series, index) => ({
        name: series.name,
        type: 'bar',
        stack: 'monetisation',
        barMaxWidth: 34,
        itemStyle: {
          color: engagementColor(series.category ?? series.name),
          ...(index === stackOrder.length - 1 ? { borderRadius: [4, 4, 0, 0] } : {}),
        },
        emphasis: { focus: 'series' },
        data: series.values,
      })),
    };
  }, [data.periods, data.series, hidden, isDark]);

  return (
    <ChartCard
      title="Monetisation Engagement"
      infoHint={ENGAGEMENT_LOCATION_HELP.monetisation}
      state={state}
      note={data.note}
      empty={data.periods.length === 0}
      {...filters}
      legend={
        <div className="flex flex-wrap items-center gap-5">
          {data.series.map((series) => (
            <LegendChip
              key={series.name}
              name={series.name}
              color={engagementColor(series.category ?? series.name)}
              hidden={Boolean(hidden[series.name])}
              onToggle={() => toggle(series.name)}
            />
          ))}
        </div>
      }
    >
      <ReactECharts option={option} notMerge style={{ height: 260, width: '100%' }} />
    </ChartCard>
  );
}

/** Figma "Community Engagement": trend lines plus a totals rail on the right. */
export function CommunityEngagementCard({
  data,
  ...filters
}: { data: TimeseriesView } & ChartFilterProps) {
  const isDark = useIsDarkMode();
  const { hidden, toggle } = useHiddenSeries();
  const state = engagementReadyState(data.availability);

  const option = useMemo(() => {
    const ink = {
      primary: tone(CHART_INK.primary, isDark),
      muted: tone(CHART_INK.muted, isDark),
      grid: tone(CHART_INK.grid, isDark),
      surface: tone(CHART_INK.surface, isDark),
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: ink.surface,
        borderColor: ink.grid,
        textStyle: { color: ink.primary },
      },
      legend: { show: false },
      grid: { left: 8, right: 16, top: 16, bottom: 8, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: data.periods,
        axisTick: { show: false },
        axisLine: { show: false },
        axisLabel: { color: ink.muted, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        splitLine: { lineStyle: { color: ink.grid, type: 'dashed' } },
        axisLabel: { color: ink.muted, fontSize: 10, formatter: (v: number) => compact.format(v) },
      },
      series: data.series
        .filter((series) => !hidden[series.name])
        .map((series) => ({
          name: series.name,
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 2 },
          itemStyle: { color: engagementColor(series.category ?? series.name) },
          data: series.values,
        })),
    };
  }, [data.periods, data.series, hidden, isDark]);

  return (
    <ChartCard
      title="Community Engagement"
      infoHint={ENGAGEMENT_LOCATION_HELP.audience}
      state={state}
      note={data.note}
      empty={data.periods.length === 0}
      {...filters}
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
        <ReactECharts option={option} notMerge style={{ height: 260, width: '100%' }} />
        <ul className="flex flex-row flex-wrap gap-4 self-center lg:w-[190px] lg:flex-col lg:gap-5">
          {data.series.map((series) => {
            const color = engagementColor(series.category ?? series.name);
            const isHidden = Boolean(hidden[series.name]);
            return (
              <li key={series.name}>
                <button
                  type="button"
                  onClick={() => toggle(series.name)}
                  aria-pressed={!isHidden}
                  className={cn(
                    'flex w-full items-center gap-3 text-sm transition-opacity',
                    isHidden ? 'opacity-40' : 'opacity-100',
                  )}
                >
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: color }}
                  >
                    <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
                  </span>
                  <span className="flex-1 text-left text-muted-foreground">{series.name}</span>
                  <span className="font-semibold tabular-nums">{compact.format(series.total)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </ChartCard>
  );
}

function ChartCard({
  title,
  infoHint,
  state,
  note,
  empty,
  legend,
  children,
  range,
  onRangeChange,
  startDate,
  endDate,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes,
  isLoading,
}: {
  title: string;
  infoHint?: string;
  state: ReturnType<typeof engagementReadyState>;
  note?: string | undefined;
  empty: boolean;
  legend?: ReactNode;
  children: ReactNode;
} & ChartFilterProps) {
  return (
    <Card className="border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="p-5 pb-3">
        <EngagementChartHeader
          title={title}
          {...(infoHint ? { infoHint } : {})}
          range={range}
          onRangeChange={onRangeChange}
          {...(startDate ? { startDate } : {})}
          {...(endDate ? { endDate } : {})}
          countryCode={countryCode}
          onCountryCodeChange={onCountryCodeChange}
          {...(availableCountryCodes ? { availableCountryCodes } : {})}
          trailing={<NotReadyBadge state={state} hint={note} />}
        />
      </CardHeader>
      <CardContent className="space-y-4 p-5 pt-0">
        {isLoading ? (
          <EngagementBusy />
        ) : (
          <>
            {legend}
            {empty ? (
              <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
                No data for this window.
              </div>
            ) : (
              children
            )}
            {note ? <NotReadyNote>{note}</NotReadyNote> : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
